const DEFAULT_PAGE_SIZE = 25;

function coerceBooleanProperty(prop) {
    return prop === '' || prop === 'on' || prop === 'yes' || prop === 'true' || prop === '1' || (typeof prop === 'number' && prop !== 0) || prop === true;
}

class Subscription {
    constructor(obj, event, callback) {
        this._obj = obj;
        this._event = event;
        this._callback = callback;
    }

    unsubscribe() {
        if (this._obj && this._event && this._callback) {
            this._obj.removeEventListener(this._event, this._callback);
        }

        this._obj = void 0;
        this._event = void 0;
        this._callback = void 0;
    }
}

class Subscriptions {
    constructor() {
        this._data = [];
    }

    add(subscription) {
        if (typeof subscription === 'object' && subscription instanceof Subscription) {
            this._data.push(subscription);
        }
    }

    unsubscribe() {
        for (const s of this._data) {
            s.unsubscribe();
        }

        this._data = [];
    }
}

const oldAddEventListener = HTMLElement.prototype.addEventListener;

HTMLElement.prototype.addEventListener = function(event, listener, options) {
    oldAddEventListener.call(this, event, listener, options);

    return new Subscription(this, event, listener);
};

class Sort {
    constructor() {
        this.data = [];
    }

    push(field, value) {
        this.data = this.data.filter(obj => obj.field !== field);

        if (value === 'asc' || value === 'desc') {
            this.data.push({field, value});
        }
    }

    toString() {
        const tmp = [];

        for (const obj of this.data) {
            tmp.push(`${encodeURIComponent(obj.field)}=${encodeURIComponent(obj.value)}`);
        }

        return tmp.join('&');
    }
}


class DataTable extends HTMLElement {
    get selectable() {
        return coerceBooleanProperty(this.getAttribute('selectable'));
    }

    constructor() {
        super();

        this._paginator = null;
        this._inputTimeout = null;
        this._sort = new Sort();
        this._observer = new MutationObserver(this.onMutation.bind(this));
        this._shadow = this.attachShadow({ mode: 'closed' });
        this._rows = [];
        this._selection = [];
        this._onPageSubscription = void 0;
        this._onSizeSubscription = void 0;

        const style = this._shadow.appendChild(document.createElement('link'));
        style.rel = 'stylesheet';
        style.href = `/data-table.css?t=${+Date.now()}`;
    }

    async connectedCallback() {
        this.table = this._shadow.appendChild(document.createElement('table'));
        this.loader = this._shadow.appendChild(document.createElement('div'));
        this.table.style.width = '100%';
        this.table.classList.add('data-table');
        this.loader.classList.add('data-table-loader');
        this.loader.appendChild(document.createElement('div'));

        setTimeout(() => { // ograniczenie niepotrzebnego odpalania wielokrotnie podczas budowania elementu...
            this._observer.observe(this, {
                attributes: true,
                childList: true,
                subtree: true
            });

            this.onMutation();
            this.load();
        });
    }

    disconnectedCallback() {
        this._observer.disconnect();
        this.table.remove();
    }

    async onPage() {
        await this.load();
    }

    async onSize() {
        await this.load();
    }

    onMutation() {
        if (this._onPageSubscription) {
            this._onPageSubscription.unsubscribe();
            this._onPageSubscription = void 0;
        }

        if (this._onSizeSubscription) {
            this._onSizeSubscription.unsubscribe();
            this._onSizeSubscription = void 0;
        }

        if ((this._paginator = this.querySelector('data-table-paginator'))) {
            this._onPageSubscription = this._paginator.addEventListener('page', this.onPage.bind(this));
            this._onSizeSubscription = this._paginator.addEventListener('size', this.onSize.bind(this));
        }

        //clear table
        while (this.table.firstChild) {
            this.table.removeChild(this.table.firstChild);
        }

        const shadowPaginator = this._shadow.querySelector('div.data-table-paginator-container');

        if (!this._paginator && shadowPaginator) {
            shadowPaginator.parentElement.removeChild(shadowPaginator);
        } else if (this._paginator && !shadowPaginator) {
            if (this._paginator._element && this._paginator._element.parentElement) {
                this._paginator._element.parentElement.removeChild(this._paginator._element);
            }

            this._shadow.appendChild(this._paginator._element);
        }

        const headers = this.querySelectorAll('data-table-header');

        const thead = this.table.appendChild(document.createElement('thead'));
        const theadRow = thead.appendChild(document.createElement('tr'));
        const tbody = this.table.appendChild(document.createElement('tbody'));
        let filterable = false;

        if (this.selectable) {
            const selectAllTh = theadRow.appendChild(document.createElement('th'));
            this.selectAllCheckbox = selectAllTh.appendChild(document.createElement('input'));
            this.selectAllCheckbox.type = 'checkbox';

            this.selectAllCheckbox.addEventListener('change', e => {
                for (const checkbox of tbody.querySelectorAll('input[data-type="select-row"]')) {
                    checkbox.checked = this.selectAllCheckbox.checked;

                    const rowId = checkbox.getAttribute('data-id');
                    const tr = checkbox.closest('tr');

                    if (rowId === void 0) {
                        console.error('Zbiór danych nie posiada pola z identyfikatorem.');
                        continue;
                    }

                    if (this.selectAllCheckbox.checked) {
                        if (!this._selection.includes(rowId)) {
                            tr.classList.add('selected');
                            this._selection.push(rowId);
                        }
                    } else {
                        if (this._selection.includes(rowId)) {
                            tr.classList.remove('selected');
                            this._selection = this._selection.filter(item => item !== rowId);
                        }
                    }
                }

                this.selectionChanged();
            });
        }

        for (const header of headers) {
            if (!header.visible) continue;

            const th = theadRow.appendChild(document.createElement('th'));
            th.innerText = th.textContent = header.label;

            if (header.filterable) {
                filterable = true;
            }

            if (header.sortable) {
                const sorter = th.appendChild(document.createElement('data-table-internal-sorter'));
                sorter.addEventListener('click', async e => {
                    e.preventDefault();

                    sorter.next();
                    await this.onSort(header, sorter);
                });
            }
        }

        if (filterable) {
            const filtersRow = thead.appendChild(document.createElement('tr'));

            if (this.selectable) {
                filtersRow.appendChild(document.createElement('th'));
            }

            for (const header of headers) {
                if (!header.visible) continue;

                const filterCell = filtersRow.appendChild(document.createElement('th'));

                if (!header.filterable) {
                    continue;
                }

                const filter = header.filter;
                const filterElement = (filter || {}).element;

                if (filter && filterElement) {
                    if (filterElement.parentElement) {
                        filterElement.parentElement.removeChild(filter);
                    }

                    filterCell.appendChild(filterElement);
                    filter.addEventListener('filter', async e => {
                        e.preventDefault();
                        e.stopPropagation();

                        await this.onFilter();
                    });
                }
            }
        }

        this.render(headers, tbody);
    }

    async onFilter() {
        if (this._inputTimeout) {
            clearTimeout(this._inputTimeout);
        }

        this._inputTimeout = setTimeout(() => {
            this._inputTimeout = null;
            this.load(true);
        }, 500);
    }

    async onSort(header, sorter) {
        this._sort.push(header.field, sorter.direction);
        await this.load();
    }

    async load(resetPaginator) {
        if (this._inputTimeout) {
            clearTimeout(this._inputTimeout);
            this._inputTimeout = null;
        }

        this.table.classList.remove('error');
        this.table.classList.add('loading');
        this.loader.classList.add('loading');

        if (this.selectAllCheckbox) {
            this.selectAllCheckbox.checked = false;
            this._selection = [];
            this.dispatchEvent(new CustomEvent('selection-changed', {detail: this._selection}));
        }

        if (this._paginator) {
            this._paginator.disabled = true;
        }

        const payload = {
            sort: this._sort.data,
            filters: this._filters
        };

        if (this._paginator) {
            payload.page = !resetPaginator ? this._paginator.page : 1;
            payload.size = this._paginator.size;
        }

        try {
            const response = await fetch(this.getAttribute('url'), {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            this._rows = data.rows || [];

            if (this._paginator) {
                // noinspection JSUnresolvedVariable
                this._paginator.update(data.total_rows || 0, !!resetPaginator);
            }
        } catch (e) {
            console.error(e);
            this.table.classList.add('error');
            this._rows = [];

            if (this._paginator) {
                this._paginator.total = 0;
            }
        } finally {
            this.render();
            this.table.classList.remove('loading');
            this.loader.classList.remove('loading');

            if (this._paginator) {
                this._paginator.disabled = false;
            }

            this.dispatchEvent(new CustomEvent('load', {
                detail: this._rows,
                bubbles: true
            }));
        }
    }

    selectionChanged() {
        if (this.selectAllCheckbox) {
            this.selectAllCheckbox.checked = this._rows.length === this._selection.length;
        }

        this.dispatchEvent(new CustomEvent('selection-changed', {detail: this._selection}));
    }

    render(headers, tbody) {
        headers = headers || this.querySelectorAll('data-table-header');
        tbody = tbody || this.table.querySelector('tbody');

        while (tbody.firstChild) {
            tbody.removeChild(tbody.firstChild);
        }

        if (this._rows.length > 0) {
            for (const row of this._rows) {
                const tr = tbody.appendChild(document.createElement('tr'));

                if (this.selectable) {
                    const selectTd = tr.appendChild(document.createElement('td'));
                    const selectCheckbox = selectTd.appendChild(document.createElement('input'));
                    selectCheckbox.type = 'checkbox';
                    selectCheckbox.setAttribute('data-type', 'select-row');
                    selectCheckbox.setAttribute('data-id', row.id);

                    // noinspection JSUnusedLocalSymbols
                    selectCheckbox.addEventListener('change', e => {
                        if (row.id === void 0) {
                            console.error('Zbiór danych nie posiada pola z identyfikatorem.');
                            return;
                        }

                        if (!this._selection.includes(row.id)) {
                            tr.classList.add('selected');
                            this._selection.push(row.id);
                        } else {
                            tr.classList.remove('selected');
                            this._selection = this._selection.filter(item => item !== row.id);
                        }

                        this.selectionChanged();
                    });
                }

                for (const header of headers) {
                    if (!header.visible) continue;

                    const td = tr.appendChild(document.createElement('td'));
                    const value = header.renderer.render(row[header.field], row);

                    if (typeof value === "number" || typeof value === "string") {
                        td.textContent = value;
                    } else if (typeof value === "boolean") {
                        td.textContent = value ? "Tak" : "Nie";
                    } else if (typeof value === "object" && value instanceof HTMLElement) {
                        td.appendChild(value);
                    } else {
                        td.textContent = "-";
                    }
                }
            }
        } else {
            const headersCount = Array.from(headers).filter(header => !!header.visible).length;
            const tr = tbody.appendChild(document.createElement('tr'));
            const td = tr.appendChild(document.createElement('td'));
            td.colSpan = headersCount;
            td.textContent = 'Brak danych.';
            td.style.textAlign = 'center';
        }

        this.dispatchEvent(new CustomEvent('render'));
    }

    get _filters() {
        const headers = this.querySelectorAll('data-table-header');
        const filters = {};

        for (const header of headers) {
            if (header.filterable) {
                const value = header.filter.value;

                if (value !== undefined) {
                    filters[header.field] = value;
                }
            }
        }

        return filters;
    }
}

class DataTableHeader extends HTMLElement {
    constructor() {
        super();

        this._defaultFilter = document.createElement('data-table-filter-default');
        this._defaultRenderer = document.createElement('data-table-renderer-default');
    }

    connectedCallback() {
        this.style.display = 'none';
        this.hidden = true;

        this.appendChild(this._defaultFilter);
        this.appendChild(this._defaultRenderer);
    }

    get filterable() {
        return coerceBooleanProperty(this.getAttribute('filterable')) || this._findFilter() !== void 0;
    }

    get filter() {
        return this._findFilter() || this._defaultFilter;
    }

    get sortable() {
        return coerceBooleanProperty(this.getAttribute('sortable'));
    }

    get field() {
        return this.getAttribute('field');
    }

    get visible() {
        const attr = this.getAttribute('visible');

        return attr === null || attr === undefined || coerceBooleanProperty(attr);
    }

    get label() {
        const filtered = Array.from(this.childNodes).filter(v => v.nodeName === '#text' || v.nodeType === Node.TEXT_NODE).filter(v => v.textContent && v.textContent.trim().length > 0);
        const txt = filtered.length > 0 ? filtered[0].textContent : '';

        return txt.trim();
    }

    get renderer() {
        return this._findRenderer() || this._defaultRenderer;
    }

    _findFilter() {
        for (const child of Array.from(this.children)) {
            if (child instanceof DataTableCustomFilter && child !== this._defaultFilter) {
                return child;
            }
        }

        return void 0;
    }

    _findRenderer() {
        for (const child of Array.from(this.children)) {
            if (child instanceof DataTableCustomRenderer && child !== this._defaultRenderer) {
                return child;
            }
        }

        return void 0;
    }
}

class DataTableSorter extends HTMLElement {
    constructor() {
        super();

        this.up = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        this.down = document.createElementNS('http://www.w3.org/2000/svg', 'svg');

        this.up.setAttribute('width', '10');
        this.up.setAttribute('height', '15');
        this.up.setAttribute('viewBox', '0 0 10 15');
        this.down.setAttribute('width', '10');
        this.down.setAttribute('height', '15');
        this.down.setAttribute('viewBox', '0 0 10 15');

        const pathUp = this.up.appendChild(document.createElementNS('http://www.w3.org/2000/svg', 'path'));
        pathUp.setAttribute('d', 'm4.99942,14.57955l-4.72013,-5.31014l3.5401,0l0,-8.85024l2.36006,0l0,8.85024l3.5401,0l-4.72013,5.31014z');

        const pathDown = this.down.appendChild(document.createElementNS('http://www.w3.org/2000/svg', 'path'));
        pathDown.setAttribute('d', pathUp.getAttribute('d'));
        pathDown.setAttribute('transform', 'rotate(180, 5, 7.49805)');
    }

    connectedCallback() {
        this.appendChild(this.up);
        this.appendChild(this.down);
    }

    disconnectedCallback() {
        this.removeChild(this.up);
        this.removeChild(this.down);
    }

    get direction() {
        if (this.classList.contains('asc')) {
            return 'asc';
        } else if (this.classList.contains('desc')) {
            return 'desc';
        }

        return void 0;
    }

    next() {
        const dir = this.direction;

        switch (dir) {
            case 'asc':
                this.classList.replace('asc', 'desc');
                break;
            case 'desc':
                this.classList.remove('desc');
                break;
            default:
                this.classList.add('asc');
                break;
        }
    }
}

class DataTablePaginator extends HTMLElement {
    get disabled() { return this._disabled; }
    set disabled(d) { this._disabled = !!d; this.render(); }

    get sizeOptions() {
        const attr = this.getAttribute('sizes') || '10,25,50,100,200';

        return attr.split(/,|;|\s+/);
    }

    constructor() {
        super();

        this._totalRecords = 0;
        this._page = 1;
        this._size = 25;
        this._disabled = false;
        this._container = document.createElement('div');
        this._container.classList.add('data-table-paginator-container');
        this._container.style.display = 'flex';
        this._container.style.justifyContent = 'end';
        this._container.style.marginTop = '10px';

        const changeSizeLabel = this._container.appendChild(document.createElement('span'));
        changeSizeLabel.textContent = 'Rekordów na stronę:';

        const selectContainer = this._container.appendChild(document.createElement('div'));
        selectContainer.classList.add('data-table-paginator-select-container');

        const selectLabel = selectContainer.appendChild(document.createElement('label'));

        this._sizesSelect = selectLabel.appendChild(document.createElement('select'));
        this._stats = this._container.appendChild(document.createElement('span'));
        this._first = this._container.appendChild(document.createElementNS('http://www.w3.org/2000/svg', 'svg'));
        this._prev = this._container.appendChild(document.createElementNS('http://www.w3.org/2000/svg', 'svg'));
        this._next = this._container.appendChild(document.createElementNS('http://www.w3.org/2000/svg', 'svg'));
        this._last = this._container.appendChild(document.createElementNS('http://www.w3.org/2000/svg', 'svg'));

        for (const el of [this._first, this._prev, this._next, this._last]) {
            el.setAttribute('viewBox', '0 0 24 24');
            el.style.width = el.style.height = '24px';
        }

        let title = this._first.appendChild(document.createElementNS('http://www.w3.org/2000/svg', 'title'));
        title.textContent = 'Pierwsza strona';
        title = this._prev.appendChild(document.createElementNS('http://www.w3.org/2000/svg', 'title'));
        title.textContent = 'Poprzednia strona';
        title = this._next.appendChild(document.createElementNS('http://www.w3.org/2000/svg', 'title'));
        title.textContent = 'Następna strona';
        title = this._last.appendChild(document.createElementNS('http://www.w3.org/2000/svg', 'title'));
        title.textContent = 'Ostatnia strona';

        let path = this._first.appendChild(document.createElementNS('http://www.w3.org/2000/svg', 'path'));
        path.setAttribute('d', "M18.41 16.59L13.82 12l4.59-4.59L17 6l-6 6 6 6zM6 6h2v12H6z");
        path = this._prev.appendChild(document.createElementNS('http://www.w3.org/2000/svg', 'path'));
        path.setAttribute('d', "M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z");
        path = this._next.appendChild(document.createElementNS('http://www.w3.org/2000/svg', 'path'));
        path.setAttribute('d', "M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z");
        path = this._last.appendChild(document.createElementNS('http://www.w3.org/2000/svg', 'path'));
        path.setAttribute('d', "M5.59 7.41L10.18 12l-4.59 4.59L7 18l6-6-6-6zM16 6h2v12h-2z");

        this._first.addEventListener('click', e => {
            e.preventDefault();
            e.stopPropagation();

            this.first();
        });

        this._next.addEventListener('click', e => {
            e.preventDefault();
            e.stopPropagation();

            this.next();
        });

        this._prev.addEventListener('click', e => {
            e.preventDefault();
            e.stopPropagation();

            this.prev();
        });

        this._last.addEventListener('click', e => {
            e.preventDefault();
            e.stopPropagation();

            this.last();
        });

        this._sizesSelect.addEventListener('change', e => {
            e.preventDefault();
            e.stopPropagation();

            this._size = +this._sizesSelect.value;
            this.dispatchEvent(new CustomEvent('size', { detail: this._sizesSelect.value }));
        });

        this.render();
    }

    get lastPage() {
        return this._size > 0 ? Math.ceil(this._totalRecords / this._size) : 0;
    }

    get page() {
        return this._page;
    }

    get size() {
        return this._size;
    }

    get _element() {
        return this._container;
    }

    connectedCallback() {
        this.hidden = true;
        this.style.display = 'none';

        if (!this.parentElement || this.parentElement.tagName.toLowerCase() !== 'data-table') {
            console.error('DataTablePaginator musi być umieszczony wewnątrz elementu <data-table>.');
            this.parentElement.removeChild(this);
        }
    }

    update(total, reset) {
        this._totalRecords = total || 0;

        if (reset) {
            this._page = 1;
        }

        this.render();
    }

    render() {
        this._first.classList.remove('disabled');
        this._prev.classList.remove('disabled');
        this._next.classList.remove('disabled');
        this._last.classList.remove('disabled');

        if (!this.hasPrev || this.disabled) {
            this._first.classList.add('disabled');
            this._prev.classList.add('disabled');
        }

        if (!this.hasNext || this.disabled) {
            this._next.classList.add('disabled');
            this._last.classList.add('disabled');
        }

        if (this._totalRecords < 1) {
            this._stats.textContent = '0 / 0';
        } else {
            const length = Math.max(this._totalRecords, 0);
            const startIndex = (this._page - 1) * this._size;

            // If the start index exceeds the list length, do not try and fix the end index to the end.
            const endIndex = startIndex < length ? Math.min(startIndex + this._size, length) : startIndex + this._size;
            this._stats.textContent = `${startIndex + 1} - ${endIndex} / ${length}`;
        }

        while (this._sizesSelect.firstChild) {
            this._sizesSelect.removeChild(this._sizesSelect.firstChild);
        }

        for (const size of this.sizeOptions) {
            const option = this._sizesSelect.appendChild(document.createElement('option'));
            option.value = option.textContent = size;

            if (parseInt(size) === this.size) {
                option.selected = true;
            }
        }
    }

    get hasNext() {
        return this._page < this.lastPage;
    }

    get hasPrev() {
        return this._page > 1;
    }

    first() {
        if (!this.hasPrev || this.disabled) {
            return;
        }

        this._page = 1;
        this.dispatchEvent(new CustomEvent('page', { detail: this._page }));
    }

    prev() {
        if (!this.hasPrev || this.disabled) {
            return;
        }

        this._page = this._page - 1;
        this.dispatchEvent(new CustomEvent('page', { detail: this._page }));
    }

    next() {
        if (!this.hasNext || this.disabled) {
            return;
        }

        this._page = this._page + 1;
        this.dispatchEvent(new CustomEvent('page', { detail: this._page }));
    }

    last() {
        if (!this.hasNext || this.disabled) {
            return;
        }

        this._page = this.lastPage;
        this.dispatchEvent(new CustomEvent('page', { detail: this._page }));
    }
}



class DataTableCustomFilter extends HTMLElement {
    get value() {
        return void 0;
    }

    get element() {
        return void 0;
    }
}

class DataTableCustomRenderer extends HTMLElement {
    render(value, row) {
        return '-';
    }
}


class DataTableDefaultFilter extends DataTableCustomFilter {
    constructor() {
        super();

        this._div = document.createElement('div');
        this._input = this._div.appendChild(document.createElement('input'));
        this._input.style.boxSizing = 'border-box';
        this._input.style.width = '100%';
        this._input.style.border = 'none';
        this._input.style.borderBottom = '1px solid lightgrey';
        this._input.type = 'text';
        this._input.setAttribute('placeholder', 'Szukaj');

        this._input.addEventListener('input', e => {
            e.preventDefault();
            e.stopPropagation();

            this.dispatchEvent(new CustomEvent('filter', {
                detail: this.value
            }));
        });
    }

    get element() {
        return this._div;
    }

    get value() {
        const value = this._input.value.trim();
        return value.length > 0 ? value : void 0;
    }
}

class DataTableSelectFilter extends DataTableCustomFilter {
    get element() {
        return this._div;
    }

    get value() {
        if (this._multiple) {
            const result = [];
            const options = select && select.options;
            let opt;

            for (let i = 0, iLen = options.length; i < iLen; i++) {
                opt = options[i];

                if (opt.selected) {
                  result.push(opt.value || opt.text);
                }
            }

            return result ? result : void 0;
        }

        return this._select.value && this._select.value.length ? this._select.value.trim() : void 0;
    }

    constructor() {
        super();

        this._multiple = this.hasAttribute('multiple');
        this._div = document.createElement('div');
        this._select = this._div.appendChild(document.createElement('select'));
        this._select.style.boxSizing = 'border-box';
        this._select.style.width = '100%';

        if (this._multiple) {
            this._select.setAttribute('multiple', true);
        }

        this._observer = new MutationObserver(() => {
            this._updateOptions();
        });

        this._select.addEventListener('change', e => {
            e.preventDefault();
            e.stopPropagation();

            this.dispatchEvent(new CustomEvent('filter', {
                detail: this.value
            }));
        });
    }

    connectedCallback() {
        setTimeout(() => {
            this._updateOptions();
            this._observer.observe(this, { childList: true });
        });
    }

    disconnectedCallback() {
        this._observer.disconnect();
    }

    _updateOptions() {
        while (this._select.firstChild) {
            this._select.removeChild(this._select.firstChild);
        }

        const options = this.querySelectorAll('data-table-select-filter-option');

        this._select.appendChild(document.createElement('option'));

        for (const option of Array.from(options)) {
            const el = this._select.appendChild(document.createElement('option'));
            el.setAttribute('value', option.value);
            el.textContent = option.text;
        }
    }
}

class DataTableCheckboxFilter extends DataTableCustomFilter {
    get element() {
        return this._div;
    }

    get value() {
        const values = [];

        for (const span of this._fieldset.childNodes) {
            for (const checkbox of span.childNodes) {
                if (checkbox instanceof HTMLInputElement && checkbox.checked) {
                    values.push(checkbox.value);
                }
            }
        }

        return values.length ? values : void 0;
    }

    constructor() {
        super();

        this._multiple = this.hasAttribute('multiple');
        this._div = document.createElement('div');
        this._fieldset =  this._div.appendChild(document.createElement('fieldset'));
        this._checkboxes = [];

        this._observer = new MutationObserver(() => {
            this._updateOptions();
        });

        this._fieldset.addEventListener('change', e => {
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLLabelElement) {
                e.preventDefault();
                e.stopPropagation();

                this.dispatchEvent(new CustomEvent('filter', {
                    detail: this.value
                }));
            }
        });
    }

    connectedCallback() {
        setTimeout(() => {
            this._updateOptions();
            this._observer.observe(this, { childList: true });
        });
    }

    disconnectedCallback() {
        this._observer.disconnect();
    }

    _updateOptions() {
        while (this._fieldset.firstChild) {
            this._fieldset.removeChild(this._fieldset.firstChild);
        }

        const options = this.querySelectorAll('data-table-select-filter-option');

        for (const option of Array.from(options)) {
            const spanEl = this._fieldset.appendChild(document.createElement('span'));
            spanEl.style.whiteSpace = 'nowrap';
            const inputEl = spanEl.appendChild(document.createElement('input'));
            inputEl.setAttribute('value', option.value);
            inputEl.setAttribute('id', 'option' + option.value);

            if (option.hasAttribute('title')) {
                spanEl.setAttribute('title', option.title);
            }

            if (option.hasAttribute('selected')) {
                inputEl.checked = true;
            }

            inputEl.setAttribute('type', 'checkbox');
            const labelEl = spanEl.appendChild(document.createElement('label'));
            labelEl.setAttribute('for', 'option' + option.value);
            labelEl.textContent = option.text;
        }
    }
}

class DataTableComboBoxMultipleFilter extends DataTableCustomFilter {
    get element() {
        return this._div;
    }

    get value() {
        const values = [];

        for (const checkbox of this._fieldList.querySelectorAll('input[type="checkbox"]')) {
            if (checkbox.checked) {
                if (this._type === 'integer') {
                    const intValue = parseInt(checkbox.value);

                    if (!isNaN(intValue)) {
                        values.push(intValue);
                    }
                } else {
                    values.push(checkbox.value);
                }
            }
        }

        return values.length ? values : void 0;
    }

    constructor() {
        super();

        this._type = this.hasAttribute('type') ? this.getAttribute('type') : null;
        this._div = document.createElement('div');
        this._decorationDiv = this._div.appendChild(document.createElement('div'));
        this._decorationTextDiv = this._decorationDiv.appendChild(document.createElement('div'));
        this._decorationArrowDiv = this._decorationDiv.appendChild(document.createElement('div'));
        this._fieldsDiv = this._div.appendChild(document.createElement('div'));
        this._fieldList = this._fieldsDiv.appendChild(document.createElement('ul'));

        const selectWidth = '150px';
        this._div.style.width = selectWidth;
        this._decorationDiv.style.border = '1px solid darkgray';
        this._decorationDiv.style.backgroundColor = '#f7f3f3';
        this._decorationDiv.style.display = 'flex';
        this._decorationDiv.style.width = selectWidth;
        this._decorationDiv.style.height = '24px';
        this._decorationDiv.style.borderRadius = '3px';
        this._decorationDiv.style.paddingLeft = '3px';
        this._decorationDiv.style.paddingRight = '3px';
        this._decorationTextDiv.style.overflow = 'hidden';
        this._decorationTextDiv.style.wordBreak = 'keep-all';
        this._decorationTextDiv.style.whiteSpace = 'nowrap';
        this._decorationTextDiv.style.textOverflow = 'ellipsis';
        this._decorationTextDiv.textContent = 'Nic nie wybrano';
        this._decorationTextDiv.style.cursor = 'default';
        this._decorationArrowDiv.style.width = '24px';

        const svg = this._decorationArrowDiv.appendChild(document.createElementNS('http://www.w3.org/2000/svg', 'svg'));
        svg.setAttribute('viewBox', '0 0 24 24');
        svg.style.height = '24px';
        svg.style.width = '24px';
        svg.style.transform = 'rotate(-90deg)';
        const svgPath = svg.appendChild(document.createElementNS('http://www.w3.org/2000/svg', 'path'));
        svgPath.setAttribute('d', 'M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z');
        this._decorationArrowDiv.style.marginLeft = 'auto';

        this._fieldsDiv.hidden = true;
        this._fieldsDiv.style.border = '1px solid black';
        this._fieldsDiv.style.minWidth = selectWidth;
        this._fieldsDiv.style.maxWidth = '200px';
        this._fieldsDiv.style.backgroundColor = 'white';
        this._fieldsDiv.style.position = 'absolute';
        this._fieldsDiv.style.paddingRight = '10px';
        this._fieldsDiv.style.textAlign = 'left';
        this._fieldsDiv.style.zIndex = 10;

        this._fieldList.style.listStyle = 'none';
        this._fieldList.style.paddingLeft = 0;

        this._windowClickCallback = e => {
            if (this._fieldsDiv.hidden) {
                return;
            }

            const decorationRect = this._decorationDiv.getBoundingClientRect();
            const fieldsRect = this._fieldsDiv.getBoundingClientRect();

            if (decorationRect.left <= e.clientX && decorationRect.top <= e.clientY && decorationRect.right >= e.clientX && decorationRect.bottom >= e.clientY) {
                return;
            }

            if (fieldsRect.left <= e.clientX && fieldsRect.top <= e.clientY && fieldsRect.right >= e.clientX && fieldsRect.bottom >= e.clientY) {
                return;
            }

            this._fieldsDiv.hidden = true;

            this.updateSelectedLabels();
        };

        this._decorationDiv.addEventListener('click', () => {
            this._fieldsDiv.hidden = !this._fieldsDiv.hidden;
        });

        window.addEventListener('click', this._windowClickCallback);

        this._observer = new MutationObserver(() => {
            this._updateOptions();
            this.updateSelectedLabels();
        });

        this._fieldList.addEventListener('change', e => {
            e.preventDefault();
            e.stopPropagation();

            this.dispatchEvent(new CustomEvent('filter', {
                detail: this.value
            }));

            this.updateSelectedLabels();
        });
    }

    connectedCallback() {
        setTimeout(() => {
            this._updateOptions();
            this.updateSelectedLabels();
            this._observer.observe(this, { childList: true });
        });
    }

    disconnectedCallback() {
        this._observer.disconnect();
    }

    _updateOptions() {
        while (this._fieldList.firstChild) {
            this._fieldList.removeChild(this._fieldList.firstChild);
        }

        const options = this.querySelectorAll('data-table-select-filter-option');

        for (const option of Array.from(options)) {
            const li = this._fieldList.appendChild(document.createElement('li'));
            li.style.whiteSpace = 'nowrap';
            li.style.overflow = 'hidden';
            li.style.textOverflow = 'ellipsis';
            const checkbox = li.appendChild(document.createElement('input'));
            checkbox.setAttribute('type', 'checkbox');
            checkbox.setAttribute('value', option.value);
            const randomId = Math.random().toString(36).replace(/[^a-z]+/g, '').substr(0, 5);
            checkbox.setAttribute('id', randomId);
            checkbox.style.marginLeft = '10px';

            if (option.hasAttribute('selected')) {
                checkbox.checked = true;
            }

            const label = li.appendChild(document.createElement('label'));
            label.setAttribute('for', randomId);
            label.setAttribute('title', option.hasAttribute('title') ? option.getAttribute('title') : option.text);
            label.style.wordBreak = 'keep-all';
            label.textContent = option.text;
        }
    }

    _getSelectedLabels() {
        const result = [];

        for (const li of this._fieldList.querySelectorAll('li')) {
            const checkbox = li.querySelector('input[type="checkbox"]');
            const label = li.querySelector('label');

            if (checkbox instanceof HTMLInputElement && checkbox.checked) {
                result.push(label.textContent);
            }
        }

        return result;
    }

    private updateSelectedLabels() {
        this._decorationTextDiv.title = this._decorationTextDiv.textContent = this._getSelectedLabels().length > 0 ? this._getSelectedLabels().join(', ') : 'Nic nie wybrano';
    }
}

customElements.define('data-table', DataTable);
customElements.define('data-table-header', DataTableHeader);
customElements.define('data-table-internal-sorter', DataTableSorter);
customElements.define('data-table-paginator', DataTablePaginator);

customElements.define('data-table-filter-default', DataTableDefaultFilter);

customElements.define('data-table-renderer-default', class extends DataTableCustomRenderer {
    render(value, row) {
        return value !== undefined && value !== null && value !== '' ? value : super.render(value, row);
    }
});

customElements.define('data-table-select-filter', DataTableSelectFilter);
customElements.define('data-table-checkbox-filter', DataTableCheckboxFilter);
customElements.define('data-table-combobox-multiple-filter', DataTableComboBoxMultipleFilter);

customElements.define('data-table-select-filter-option', class extends HTMLElement {
    get value() {
        return this.getAttribute('value');
    }

    get text() {
        return `${this.textContent}`.trim();
    }
});

customElements.define('data-table-select-integer-filter', class extends DataTableSelectFilter {
    get value() {
        if (this._multiple) {
            const result = [];
            const options = this._select && this._select.options;
            let opt;

            for (let i = 0, iLen = options.length; i < iLen; i++) {
                opt = options[i];

                if (opt.selected) {
                    let value = opt.value || opt.text;
                    let intValue = parseInt(value);

                    if (!isNaN(intValue)) {
                        result.push(intValue);
                    }
                }
            }

            return result.length ? result : void 0;
        }

        if (this._select.value && this._select.value.length) {
            let intValue = parseInt(this._select.value.trim());

            if (!isNaN(intValue)) {
                return intValue;
            }
        }

        return void 0;
    }
});

customElements.define('data-table-checkbox-integer-filter', class extends DataTableCheckboxFilter {
    get value() {
        const values = [];

        for (const span of this._fieldset.childNodes) {
            for (const checkbox of span.childNodes) {
                if (checkbox instanceof HTMLInputElement && checkbox.checked) {
                    const intValue = parseInt(checkbox.value);

                    if (!isNaN(intValue)) {
                        values.push(intValue);
                    }
                }
            }
        }

        return values.length ? values : void 0;
    }
});

customElements.define('data-table-severity-renderer', class extends DataTableCustomRenderer {
    render(value, row) {
        const span = document.createElement('span');
        span.textContent = value;
        span.style.border = '1px solid black';
        span.style.borderRadius = '5px';
        span.style.padding = '5px 10px';
        span.style.fontWeight = 'bold';

        switch ((value || '').toLowerCase()) {
            case 'krytyczna':
                span.style.backgroundColor = 'red';
                span.style.color = 'white';
                break;
            case 'wysoka':
                span.style.backgroundColor = 'orange';
                span.style.color = 'white';
                break;
            case 'średnia':
                span.style.backgroundColor = 'yellow';
                break;
            case 'niska':
                break;
            case 'log':
                break;
            case 'info':
                break;
        }

        return span;
    }
});

customElements.define('data-table-string-list-renderer', class extends DataTableCustomRenderer {
    constructor(...args) {
        super(...args);

        if (this.hasAttribute('char-limit')) {
            this.charLimit = parseInt(this.getAttribute('char-limit'));
        } else {
            this.charLimit = null;
        }
    }
    render(value, row) {
        if (!value) {
            return '-';
        }

        const ul = document.createElement('ul');

        for (const valueElement of value) {
            const li = document.createElement('li');

            const ellipsisSuffix = '...';

            if (this.charLimit !== null && valueElement.length > this.charLimit) {
                li.textContent = valueElement.substr(0, this.charLimit - ellipsisSuffix.length) + ellipsisSuffix;
            } else {
                li.textContent = valueElement;
            }

            ul.appendChild(li);
        }

        return ul;
    }
});

customElements.define('data-table-url-renderer', class extends DataTableCustomRenderer {
    render(value, row) {
        if (!value) {
            return '-';
        }

        const link = document.createElement('a');

        link.href = value;
        link.textContent = value;

        return link;
    }
});

// For FUN
customElements.define('data-table-italic-bold-renderer', class extends DataTableCustomRenderer {
    render(value, row) {
        const el = document.createElement('i');
        el.appendChild(document.createElement('b')).textContent = value;

        return el;
    }
});

customElements.define('data-table-actions-renderer', class extends DataTableCustomRenderer
{
    render(value, row) {
        const el = document.createElement('span');

        for (const action of value.split(';')) {
            const actionParts = action.split('^');
            const label = actionParts[0];
            const url = actionParts[1];
            const tooltip = actionParts[2];
            const a = document.createElement('a');
            a.href = url;
            a.textContent = label;

            if (typeof tooltip !== 'undefined') {
                a.title = tooltip;
            }

            el.appendChild(a);
            el.appendChild(document.createTextNode(' '));
        }

        return el;
    }
});
